import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/loading-spinner/LoadingSpinner";

const LinkedinCallback = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountsError, setAccountsError] = useState(null);
  const [tokens, setTokens] = useState(null);

  const hasRun = React.useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const exchangeCode = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");

      if (!code) {
        return navigate("/channels?error=no-code");
      }

      try {
        const redirectUri = `${window.location.origin}/auth/linkedin/callback`;
        const userId =
          user?.userId ||
          user?.username ||
          user?.sub ||
          localStorage.getItem("mb_userId");

        if (!userId) {
          console.warn("[LinkedinCallback] No userId found – aborting token exchange");
          return navigate("/channels?error=no-user");
        }

        const endpointBase = `${process.env.REACT_APP_API_URL}/auth`;
        const resp = await axios.post(`${endpointBase}/linkedin/exchange-code`, {
          code,
          redirectUri,
          userId,
        });

        setTokens(resp.data.tokens);
        setAccounts(resp.data.adAccounts || []);
        setAccountsError(resp.data.adAccountsError || null);
        setLoading(false);
      } catch (err) {
        console.error("LinkedIn token exchange failed:", err);
        setError(err.response?.data?.error || "Token exchange failed");
        setLoading(false);
      }
    };

    exchangeCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = async (adAccountId) => {
    if (!tokens) return;
    try {
      setLoading(true);
      const endpointBase = `${process.env.REACT_APP_API_URL}/auth`;
      const userId =
        user?.userId ||
        user?.username ||
        user?.sub ||
        localStorage.getItem("mb_userId");

      await axios.post(`${endpointBase}/linkedin/complete-auth`, {
        userId,
        adAccountId,
        ...tokens,
      });

      navigate("/channels?connected=linkedin");
    } catch (err) {
      console.error("Failed to complete LinkedIn auth:", err);
      setError(err.response?.data?.error || "Failed to complete authentication");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="mb-4 text-xl font-bold">Select your LinkedIn Ad Account</h2>
      {accounts.length === 0 ? (
        accountsError ? (
          <div className="space-y-1">
            <p className="text-red-500">{accountsError.message}</p>
            {accountsError.status ? (
              <p className="text-sm text-muted-foreground">
                LinkedIn responded with status {accountsError.status}.
              </p>
            ) : null}
          </div>
        ) : (
          <p>No LinkedIn ad accounts were found for your user.</p>
        )
      ) : (
        <ul className="space-y-2">
          {accounts.map((acc) => (
            <li key={acc.accountId || acc.id}>
              <button
                className="w-full rounded border px-4 py-2 text-left hover:bg-accent"
                onClick={() => handleSelect(acc.accountId || acc.id)}
              >
                {acc.name || acc.accountId || acc.id}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LinkedinCallback;
